import { useState, useEffect, useMemo } from "react"
import { useParams } from "react-router-dom"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { cloneDeep } from "lodash"

// components
import Modal from "components/Modal/Modal"
import State from "./State/State"
import PrimeField from "components/PrimeField/PrimeField"
import { Tooltip } from "antd"

// context
import useWorkflowTracker from "context/hooks/useWorkflowTracker"
import useTeam from "utilities/useTeam"
import useWorkflowTeam from "components/WorkflowModalForm/utils/useWorkflowTeam"

// apis
import { usePutWorkflowTracker } from "api/hooks/workflows/useWorkflowsApi"
import { useUserSettings } from "api/hooks"

// styles
import "./EditWorkflowModal.scss"

dayjs.extend(utc)

const EditWorkflowModal = ({ data = {}, type, team }) => {
  const {
    workflowId,
    workflowModel,
    openEditStatesModal,
    setOpenEditStatesPanel,
    workflowTrackerData,
    isOnHold,
    invalidateParentQueries,
    isWorkflowComplete,
  } = useWorkflowTracker()
  const { studyId } = useParams()
  const { data: userSettings } = useUserSettings()
  const putWorkflowTracker = usePutWorkflowTracker({ workflowId: Number(workflowId), workflowModel })
  const { teamUsersToStr } = useTeam()
  const { teamOptions } = useWorkflowTeam()

  const [dueDateType, setDueDateType] = useState()
  const [dueDateValue, setDueDateValue] = useState()
  const [manualDueDateValue, setManualDueDateValue] = useState()
  const [stateData, setStateData] = useState([])
  const [assessmentDateItems, setAssessmentDateItems] = useState([])
  const [isOutOfScope, setIsOutOfScope] = useState(false)
  const [isOutOfScopeAssessmentName, setIsOutOfScopeAssessmentName] = useState("")
  const [selectedAssessment, setSelectedAssessment] = useState()
  const [isSubmitLoading, setIsSubmitLoading] = useState(false)

  const userItems = useMemo(() => {
    return teamUsersToStr(team)
  }, [team])

  const resetState = () => {
    setDueDateType(null)
    setDueDateValue(null)
    setManualDueDateValue(null)
    setStateData([])
    setAssessmentDateItems([])
    setIsOutOfScope(false)
    setIsOutOfScopeAssessmentName("")
    setSelectedAssessment(null)
  }

  useEffect(() => {
    if (workflowTrackerData?.state_data?.tracker_data && openEditStatesModal) {
      init()
    } else {
      resetState()
    }
  }, [workflowTrackerData?.state_data?.tracker_data, openEditStatesModal])

  useEffect(() => {
    if (workflowModel === "request" && workflowTrackerData?.parent_data?.request_due_date && openEditStatesModal) {
      setDueDateType("manual")
      setDueDateValue({ value: "manual-due-date", label: "Manual due date" })
      setManualDueDateValue(dayjs(workflowTrackerData?.parent_data?.request_due_date).format("YYYY-MM-DD"))
    }
  }, [workflowModel, workflowTrackerData?.parent_data, openEditStatesModal])

  const init = () => {
    let assessmentDateItems = []
    let ogTracker = cloneDeep(workflowTrackerData?.state_data?.tracker_data)
    if (workflowTrackerData?.parent_data?.attached_assessments) {
      assessmentDateItems = workflowTrackerData.parent_data.attached_assessments.map((assessment) => {
        let dueDate = assessment.assessment_due_date
        if (!dueDate) dueDate = "N/A"
        else dueDate = dayjs(dueDate).format(userSettings?.date_format)

        return {
          value: assessment.assessment_pk,
          label: assessment.assessment_name + " Due Date - " + dueDate,
        }
      })

      validateAssessmentDecision(
        workflowTrackerData?.parent_data?.attached_assessments,
        workflowTrackerData?.parent_data?.linked_assessment_dict?.assessment_pk
      )
    }
    let formattedStates = ogTracker.slice(1)

    for (let i = 0; i < formattedStates.length; i++) {
      let prev = formattedStates[i - 1]
      let curr = formattedStates[i]
      let next = formattedStates[i + 1]
      if (curr.is_current === true && !curr.end_state_date) {
        curr.end_state_date = next?.start_state_date
      }

      if (curr.status === "Bypassed") {
        formattedStates[i].goal_days = 0
        if (prev) {
          formattedStates[i].start_state_date = prev.end_state_date
          formattedStates[i].end_state_date = curr.start_state_date
        }
      }
    }

    setStateData(formattedStates)
    setAssessmentDateItems(assessmentDateItems)
  }

  /*** 
  this function validates linked assessment objects/manual dates and handles error message if assessment is out of scope
***/
  const validateAssessmentDecision = (assessments, selectedAssessmentUniqid) => {
    if (workflowModel === "activity") {
      const foundAssessment =
        assessments && assessments.find((assessment) => assessment.assessment_pk === selectedAssessmentUniqid)

      if (foundAssessment) {
        let dueDate = foundAssessment.assessment_due_date
        if (!dueDate) dueDate = "N/A"
        else dueDate = dayjs(dueDate).format(userSettings?.date_format)

        const decision = foundAssessment.assessment_decision

        if (decision) {
          setDueDateType("assessment")
          setIsOutOfScope(false)
          setSelectedAssessment(foundAssessment)
          setIsOutOfScopeAssessmentName("")
          setDueDateValue({
            value: foundAssessment.assessment_pk,
            label: foundAssessment.assessment_name + " Due Date - " + dueDate,
          })
        } else {
          setDueDateType("manual")
          setDueDateValue({ value: "manual-due-date", label: "Manual due date" })
          setManualDueDateValue(workflowTrackerData.parent_data.activity_due_date)
          setSelectedAssessment(null)
          setIsOutOfScope(true)
          setIsOutOfScopeAssessmentName(foundAssessment.assessment_name)
        }
      } else {
        if (workflowTrackerData.parent_data?.activity_due_date) {
          setDueDateType("manual")
          setDueDateValue({ value: "manual-due-date", label: "Manual due date" })
          setManualDueDateValue(workflowTrackerData.parent_data.activity_due_date)
          setSelectedAssessment(null)
        } else {
          setDueDateType("assessment")
          setDueDateValue(null)
          setManualDueDateValue(null)
          setSelectedAssessment(null)
        }
      }
    }
  }

  useEffect(() => {
    if (workflowTrackerData?.state_data?.tracker_data) {
      init()
    }
  }, [workflowTrackerData?.state_data?.tracker_data])

  const handleChange = (key, value, index) => {
    let tempData = [...stateData]
    let currState = tempData[index]
    currState[key] = value

    const getDifferenceBetweenDays = (a, b) => {
      return dayjs(a).diff(dayjs(b), "days")
    }

    const addDays = (date, days) => {
      return dayjs(date).add(days, "day").format("YYYY-MM-DD")
    }

    if (key !== "assigned_to_ids") {
      const lastState = tempData[tempData.length - 1]
      const secondToLastState = tempData[tempData.length - 2]
      if (secondToLastState) {
        lastState.start_state_date = secondToLastState.end_state_date
      }
      lastState.end_state_date = lastState.start_state_date
      lastState.goal_days = 0

      let goalDays = 0

      if (key === "start_state_date") {
        tempData[index].start_state_date = value
        goalDays = getDifferenceBetweenDays(currState.end_state_date, value)
      } else if (key === "end_state_date") {
        goalDays = getDifferenceBetweenDays(value, currState.start_state_date)
      } else {
        goalDays = value
      }

      if (goalDays < 0) goalDays = 0
      tempData[index].goal_days = goalDays

      for (let i = index; i < tempData.length; i++) {
        let currState = tempData[i]
        let nextState = tempData[i + 1]
        currState.end_state_date = addDays(currState.start_state_date, currState.goal_days)

        if (nextState) nextState.start_state_date = currState.end_state_date
      }
    }

    setStateData(tempData)
  }

  const onDueDateChange = (type, evt) => {
    if (type === "manual") {
      const md = dayjs(evt).toDate()
      setDueDateType("manual")
      setManualDueDateValue(dayjs(md).format("YYYY-MM-DD"))
      setIsOutOfScope(false)
    } else if (type === "assessment" && evt === "manual-due-date") {
      setDueDateType("manual")
      setDueDateValue({ value: "manual-due-date", label: "Manual due date" })
      setManualDueDateValue(null)
      setIsOutOfScope(false)
    } else {
      validateAssessmentDecision(workflowTrackerData.parent_data.attached_assessments, evt)
    }
  }

  const formatPostRequest = () => {
    let activityDueDate = null
    let assessmentPk = null
    if (dueDateType === "manual") {
      activityDueDate = dayjs(manualDueDateValue).format("YYYY-MM-DD")
    } else if (selectedAssessment?.assessment_pk && selectedAssessment?.assessment_due_date) {
      assessmentPk = selectedAssessment.assessment_pk
      activityDueDate = dayjs(selectedAssessment.assessment_due_date).format("YYYY-MM-DD")
    }

    // Current UTC time
    const currentUtcTime = dayjs().utc()

    const formatToUtc = (date) => {
      return date
        .utc()
        .hour(currentUtcTime.hour())
        .minute(currentUtcTime.minute())
        .second(currentUtcTime.second())
        .millisecond(currentUtcTime.millisecond())
    }

    let projectedCompletionDate = stateData[stateData.length - 1].start_state_date
    const lastState = stateData[stateData.length - 1]

    let actionData = stateData.map((state, i) => {
      let currState = { ...state }

      const startDate = state?.start_state_date
      const endDate = stateData?.[i + 1]?.start_state_date

      let goalDays = dayjs(endDate).diff(dayjs(startDate), "days")
      const isLast = i === stateData.length - 1
      if (isLast) goalDays = 0

      let formattedEndDate = formatToUtc(dayjs(endDate))
      if (isLast) {
        formattedEndDate = formatToUtc(dayjs(startDate))
      }

      let updatedState = {
        ...currState,
        is_first_state: i === 0,
        is_last_state: isLast,
        assign_to_ids: currState.assigned_to_ids || [],
        start_state_date: formatToUtc(dayjs(startDate)),
        end_state_date: formattedEndDate,
        goal_days: goalDays || 0,
      }

      delete updatedState.widthPercent
      delete updatedState.assigned_to_ids
      delete updatedState.assigned_to_names

      return updatedState
    })

    let req = {
      model: workflowModel,
      projected_completion_date: projectedCompletionDate,
      action_data: actionData,
    }

    if (activityDueDate) req.due_date = activityDueDate
    if (assessmentPk) req.assessment_id = assessmentPk
    if (workflowTrackerData?.parent_data?.actual_completion_date)
      req.actual_completion_date = workflowTrackerData.parent_data.actual_completion_date
    if (isWorkflowComplete) req.actual_completion_date = dayjs(lastState.start_state_date).format("YYYY-MM-DD")

    return req
  }

  const onSaveChanges = () => {
    setIsSubmitLoading(true)
    let req = formatPostRequest()
    putWorkflowTracker(
      {
        body: req,
      },
      {
        onSuccess: (resp) => {
          invalidateParentQueries()
          setOpenEditStatesPanel(false)
          if (type === "study") {
            window.open(`/study/${studyId}/activity/${data.activity_id}`, "_blank")
          }
        },
        onSettled: () => {
          setIsSubmitLoading(false)
        },
      }
    )
  }

  return (
    <Modal
      title={"Edit steps and due date"}
      message={`Adjust duration, start/end dates, and assignees. Edit one step at a time.`}
      open={openEditStatesModal}
      onCancel={() => setOpenEditStatesPanel(false)}
      okText="Save Changes"
      onOk={onSaveChanges}
      destroyOnClose={true}
      hideImage={true}
      width={1120}
      confirmLoading={isSubmitLoading}
    >
      <div className="edit-workflow-content">
        <div className="states-container">
          {stateData.map((state, index) => {
            return (
              <State
                key={"state-" + index}
                index={index}
                isOnHold={isOnHold}
                holdStateIndex={stateData.findIndex((state) => state.state_title === "On Hold" && state.is_current)}
                handleChange={handleChange}
                stateData={stateData}
                isLast={stateData.length - 1 === index}
                state={state}
                teamOptions={teamOptions}
                type={type}
              />
            )
          })}
        </div>

        <div className="workflow-dates">
          <div className="title">
            {workflowTrackerData?.parent_data?.actual_completion_date
              ? "Actual Completion Date"
              : "Projected Completion Date"}
          </div>
          <p className="subtitle">Determined by the end date of the last step</p>
          <p className="date">
            {dayjs(stateData?.[stateData?.length - 1]?.start_state_date).format(userSettings?.date_format)}
          </p>
          <div className="title">
            Edit Due Date
            <Tooltip
              title={
                <div style={{ textAlign: "left" }}>
                  The date selected as the due date determines the overall status of an{" "}
                  {type === "request" ? "request" : "activity"}
                </div>
              }
            >
              <i
                style={{ marginLeft: 7 }}
                className="fal fa-info-circle"
              />
            </Tooltip>
          </div>
          {workflowModel === "activity" && (
            <p className="subtitle">
              Select which date to use as the due date. By choosing an assessment due date, any changes to that
              assessment result will update this activity’s due date. If a manual due date is entered, this due date
              will not be linked to any assessment due date.
            </p>
          )}
        </div>

        <div className="workflow-due-dates">
          <PrimeField
            schema={{
              type: "dropdown",
              placeholder: "Select due date type",
              items: [...assessmentDateItems, { value: "manual-due-date", label: "Manual due date" }],
              label: "Due date type",
              orientation: "vertical",
            }}
            value={dueDateValue}
            onChange={(evt) => onDueDateChange("assessment", evt)}
            className="dropdowns"
          />
          {dueDateType === "manual" && (
            <PrimeField
              schema={{ type: "date" }}
              value={manualDueDateValue}
              onChange={(val) => setManualDueDateValue(val)}
              className="date-field"
            />
          )}
        </div>

        {isOutOfScope && (
          <div className="out-scope-msg">
            <i
              style={{ marginRight: 8 }}
              className="fas fa-info-circle"
            />
            The {isOutOfScopeAssessmentName} is no longer mandatory so that date has been changed to a manual date
          </div>
        )}
      </div>
    </Modal>
  )
}

export default EditWorkflowModal
